import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { UpdateParentInformationForm, UpdateParentInformationSchema, Input } from '$features/pets/forms/update-parent-information/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, clientUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditParentInformationModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updateParent, isPending: isUpdatingParent } = useMutation({
    ...clientUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
          query: {
            [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
          },
        }),
      })
      ToastHelper.success('Parent information updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update parent information.'),
  })

  const handleClose = () => navigate(-1)

  const pet = data?.data

  if (!pet) {
    return null
  }

  const handleSubmit = async (values: UpdateParentInformationSchema) => {
    updateParent({
      path: {
        client: pet.parent.id,
      },
      // @ts-expect-error - TODO: Generated API types have incorrect body type
      body: values,
    })
  }

  return (
    <UpdateParentInformationForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingParent}
      defaultValues={{
        [Input.FirstName]: pet.parent.first_name,
        [Input.LastName]: pet.parent.last_name,
        [Input.Email]: pet.parent.email,
        [Input.Phone]: pet.parent.phone ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Parent Information
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdateParentInformationForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <UpdateParentInformationForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </UpdateParentInformationForm>
  )
}
